import { useState, useEffect, useMemo } from 'react';
import { 
  DgtPdOrderPicklistExtraReturn,
  ExtraReturnStatus,
  DgtPdOrderPicklist
} from '../models'

import { getDates } from '../utilities/Format'

import {
  extraPicklistsByOrderId,
  getRequestedExtraPicklists,
  getPdApprovedExtraPicklists,
  getCanceledExtraPicklists
} from '../states/PicklistAction'

export default function usePicklistExtra(
  status: ExtraReturnStatus,
  orderType: string,
  searchByOrder: string,
  division?: string,
  orderId?: string,
  dateFrom?: Date | null,
  dateTo?: Date | null 
) : [ DgtPdOrderPicklistExtraReturn[], (updatedExtra: DgtPdOrderPicklistExtraReturn) => void] {
  const [extras, setExtras] = useState<DgtPdOrderPicklistExtraReturn[]>([]);

  const mutateExtra = (updatedExtra: DgtPdOrderPicklistExtraReturn) => {
    // If the extra is already in the list, update it. Else, add it.
    const existingExtra = extras.find(extra => extra.id === updatedExtra.id)
    if (existingExtra) {
      setExtras(prev => prev
        .map(extra => extra.id === updatedExtra.id ? updatedExtra : extra)
        .filter(extra => extra.status === status)
      )
    } else {
      setExtras(prev => [...prev, updatedExtra])
    }
  }

  useEffect(() => {
    if (orderId) {
      Promise.resolve(extraPicklistsByOrderId(orderId))
        .then(exts => setExtras(exts))
        .catch(err => console.error(err))
    } else {
      const periodDates = getDates(dateFrom, dateTo)
      Promise.allSettled(periodDates.map(date => {
        if (status === ExtraReturnStatus.NEW) {
          return getRequestedExtraPicklists(date, orderType)
        } else if (status === ExtraReturnStatus.APPROVED) {
          return getPdApprovedExtraPicklists(date, orderType)
        } else if (status === ExtraReturnStatus.CANCELED) {
          return getCanceledExtraPicklists(date, orderType)
        }
        return Promise.reject(`Picklist Status not supported : ${status}`)
      })).then(results => {
        const totalExtras = (results
          .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<DgtPdOrderPicklistExtraReturn[]>[])
          .reduce((prev, cur) => [...prev, ...cur.value], [] as DgtPdOrderPicklistExtraReturn[])
        
        const filterByDivisionExtras = totalExtras.filter(item => {
          const orderInfo : DgtPdOrderPicklist = JSON.parse(item.orderInfo)
          return orderInfo.division === division
        })
        filterByDivisionExtras.sort((a, b) => a.requestedDateTime! > b.requestedDateTime! ? 1 : -1)
        setExtras(filterByDivisionExtras)
      })
      .catch(err => console.error(err))
    }
    
  }, [ status, orderType, dateFrom, dateTo, division, orderId ]);

  const filteredExtras = useMemo(() => extras.filter(ex => ex.orderId.includes(searchByOrder)), [ searchByOrder, extras ])
  return [ filteredExtras, mutateExtra ]
}